import { apiFetchJson } from '@wundo/client/api.ts';
import type { PaginatedResponse } from '@/types/pagination.ts';

export type ChangeLogAction = 'create' | 'update' | 'delete';

/**
 * One recorded edit. `data` is the summary the list carries — the field names
 * for an update, the whole record for a create or a delete. The before/after
 * values live behind `/change-logs/:id/diff`, one row at a time.
 */
export interface ChangeLog {
  id: number;
  entity_type: string;
  entity_id: string;
  action: ChangeLogAction;
  user_id: string | null;
  user_name: string | null;
  data: Record<string, unknown> | null;
  created_at: string;
}

export interface ChangeLogDiff {
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export interface ChangeLogListParams {
  page?: number;
  limit?: number;
  /** Comma-joined by the caller's filter; the backend splits it. */
  entityTypes?: string[];
  actions?: string[];
  entityId?: string;
  userId?: string;
}

export async function listChangeLogs(
  params: ChangeLogListParams = {},
): Promise<PaginatedResponse<ChangeLog>> {
  const search = new URLSearchParams();

  search.set('page', String(params.page ?? 1));
  search.set('limit', String(params.limit ?? 25));
  if (params.entityTypes?.length) search.set('entity_type', params.entityTypes.join(','));
  if (params.actions?.length) search.set('action', params.actions.join(','));
  if (params.entityId) search.set('entity_id', params.entityId);
  if (params.userId) search.set('user_id', params.userId);

  return apiFetchJson<PaginatedResponse<ChangeLog>>(`/change-logs?${search.toString()}`);
}

export async function fetchChangeLogDiff(id: number): Promise<ChangeLogDiff> {
  return apiFetchJson<ChangeLogDiff>(`/change-logs/${id}/diff`);
}
